
import {defineComponent, PropType} from "vue";
import {CssDimensionRestDtoModel} from "@/models/api/pharma-cms-content-page/CssDimensionRestDtoModel";
import {CssUnitEnum} from "@/api/enums/css-unit-enum";
import InputCheckboxBoolean from "@/components/UI/InputCheckboxBoolean.vue";
import InputNumber from "@/components/UI/InputNumber.vue";
import {ComponentMixin} from "@/components/base/ComponentMixin";
import GenericComponentWrapper from "@/components/base/GenericComponentWrapper.vue";
import {CssDimensionRestDto} from "@/api/pharma-cms-content-page/models";

export default defineComponent({
  name: "CssDimensionEditor",
  mixins: [ComponentMixin],
  components: {GenericComponentWrapper, InputCheckboxBoolean, InputNumber},
  emits: ['update:modelValue'],
  props: {
    modelValue: {
      type: Object as PropType<CssDimensionRestDto | null>,
      default: null,
    },
    // Leave out where the dimension already has a heading of its own, e.g. a single dimension
    // underneath a subtitle. Required whenever allowDisable is on: there the label is what the
    // enable/disable checkbox is called.
    label: {
      type: String,
      required: false,
      default: '',
    },
    name: {
      type: String,
      required: true,
    },
    errors: {
      type: Object,
      required: true,
    },
    // Minimum value for the numeric input. Leave undefined to allow negative
    // values (e.g. box-shadow offsets); pass 0 to forbid negatives (e.g. blur radius).
    min: {
      type: Number,
      required: false,
    },
    // When false, the dimension is always shown and mandatory: the enable/disable
    // checkbox is hidden and a plain label is rendered instead. Used for required
    // dimensions such as box-shadow horizontal/vertical offsets.
    allowDisable: {
      type: Boolean,
      required: false,
      default: true,
    },
    // When set, the unit is not selectable: the dimension always uses this unit and
    // it is rendered as a read-only addon behind the numeric input (e.g. image
    // rounding, which is always expressed in px). When omitted, the unit dropdown
    // is rendered as usual.
    fixedUnit: {
      type: String as PropType<CssUnitEnum>,
      required: false,
      default: null,
    },
    // When true, an empty numeric input is a valid state meaning "no dimension at
    // all": an unset dimension renders as an empty field instead of 0, and clearing
    // the field emits null. Without this, clearing it would emit a dimension whose
    // value is NaN, which the API rejects. Use for optional dimensions such as a
    // maximum height; leave false where 0 is a meaningful value (rounding, offsets).
    allowEmptyValue: {
      type: Boolean,
      required: false,
      default: false,
    },
    // Shown in the numeric input while it is empty. Use together with allowEmptyValue to say what
    // an empty field means, e.g. "Standaard" for a dimension that falls back to the stylesheet
    // when it is not set.
    placeholder: {
      type: String,
      required: false,
      default: undefined,
    },
  },
  mounted() {
    this.componentUI.setReady();
  },
  data() {
    return {
      enabled: this.allowDisable ? !!this.modelValue : true,
      localDto: CssDimensionRestDtoModel.createWithDefaults() as CssDimensionRestDto,
      unitOptions: [
        {code: CssUnitEnum.PX, description: 'px'},
        {code: CssUnitEnum.REM, description: 'rem'},
        {code: CssUnitEnum.VH, description: 'vh'},
        {code: CssUnitEnum.SVH, description: 'svh'},
        {code: CssUnitEnum.PERC, description: '%'},
      ],
    };
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newValue: CssDimensionRestDto | null) {
        this.enabled = this.allowDisable ? !!newValue : true;
        this.localDto = newValue ? CssDimensionRestDtoModel.cloneFrom(newValue) : this.createDefaultDto();
        this.applyFixedUnit();
      },
    },
  },
  computed: {
    valueLabel(): string | null {
      // The enable/disable checkbox already shows the dimension name, and value and
      // unit read as a single control, so the input needs no label of its own there.
      // Without that checkbox the input is the only thing that can carry the name --
      // unless the caller gave the dimension a heading and left the label out.
      return this.allowDisable ? null : (this.label || null);
    },
    // The label distinguishes the dimensions of a group that shares one name ("Rand links" ->
    // "Rand links Breedte"). A dimension without a label is the only one under its name.
    fieldName(): string {
      return this.label ? `${this.name} ${this.label}` : this.name;
    },
    unitLabel(): string {
      return `${this.name} ${this.$t('cms.dimensionUnit')}`;
    },
    fixedUnitLabel(): string | null {
      if (!this.fixedUnit) {
        return null;
      }

      const fixedUnitOption = this.unitOptions.find(option => option.code === this.fixedUnit);

      return fixedUnitOption ? fixedUnitOption.description : this.fixedUnit;
    },
  },
  methods: {
    createDefaultDto(): CssDimensionRestDto {
      const defaultDto = CssDimensionRestDtoModel.createWithDefaults();
      if (this.fixedUnit) {
        defaultDto.unit_dimension.unit_code = this.fixedUnit;
      }
      if (this.allowEmptyValue) {
        // Renders as an empty field; emitModelChanged() turns it back into "no dimension".
        defaultDto.unit_dimension.value = null as unknown as number;
      }

      return defaultDto;
    },
    applyFixedUnit(): void {
      if (this.fixedUnit) {
        this.localDto.unit_dimension.unit_code = this.fixedUnit;
      }
    },
    onUnitChanged(event: Event): void {
      this.localDto.unit_dimension.unit_code = (event.target as HTMLSelectElement).value as CssUnitEnum;
      this.emitModelChanged();
    },
    onEnabledChanged(newValue: boolean): void {
      if (newValue) {
        this.localDto = this.createDefaultDto();
        this.$emit('update:modelValue', this.localDto);
      } else {
        this.$emit('update:modelValue', null);
      }
    },
    emitModelChanged(): void {
      // An emptied input arrives as NaN (parseFloat('')), which would be serialized
      // as null and rejected by the API as a missing value.
      if (this.allowEmptyValue && !Number.isFinite(this.localDto.unit_dimension.value)) {
        this.$emit('update:modelValue', null);
        return;
      }

      this.$emit('update:modelValue', this.localDto);
    },
  },
});
